import { useForm as useReactHookForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { useProjects } from '@/shared/hooks/useProjects';
import { useBoards, useBoardStatuses, useBoardGroups } from '@/features/boards/public';
import { useMembers } from '@/features/members/public';
import { useCreateTaskFromSubmission } from '../../hooks/useCreateTaskFromSubmission';
import { createTaskFromSubmissionSchema, type CreateTaskFromSubmissionInput } from '../../schemas';

interface CreateTaskFromSubmissionDialogProps {
  formId: string;
  submissionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateTaskFromSubmissionDialog({
  formId,
  submissionId,
  open,
  onOpenChange,
}: CreateTaskFromSubmissionDialogProps) {
  const { t } = useTranslation('forms');
  const { data: projects = [] } = useProjects();
  const createTask = useCreateTaskFromSubmission(formId, submissionId);
  const { data: members = [] } = useMembers('', 0, 100);

  const form = useReactHookForm<CreateTaskFromSubmissionInput>({
    resolver: zodResolver(createTaskFromSubmissionSchema),
    defaultValues: { projectId: '', boardId: '', statusId: '', groupId: '', assigneeId: '' },
  });

  const projectId = form.watch('projectId');
  const boardId = form.watch('boardId');

  const { boards } = useBoards(projectId);
  const { statuses } = useBoardStatuses(boardId);
  const { groups } = useBoardGroups(boardId);

  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      await createTask.mutateAsync({
        projectId: data.projectId,
        boardId: data.boardId,
        statusId: data.statusId,
        groupId: data.groupId || null,
        assigneeId: data.assigneeId || null,
      });
      form.reset();
      onOpenChange(false);
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full h-full max-w-none rounded-none md:h-auto md:max-w-lg md:rounded-lg"
        data-testid="forms-create-task-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('submissions.createTask.title')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('submissions.createTask.projectLabel')}
            </label>
            <Select
              value={projectId}
              onValueChange={(value) => {
                form.setValue('projectId', value);
                form.setValue('boardId', '');
              }}
            >
              <SelectTrigger data-testid="forms-create-task-project-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id ?? ''} value={project.id ?? ''}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t('submissions.createTask.boardLabel')}</label>
            <Select
              value={boardId}
              onValueChange={(value) => form.setValue('boardId', value)}
              disabled={!projectId}
            >
              <SelectTrigger data-testid="forms-create-task-board-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {boards.map((board) => (
                  <SelectItem key={board.id ?? ''} value={board.id ?? ''}>
                    {board.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t('submissions.createTask.statusLabel')}</label>
            <Select
              value={form.watch('statusId')}
              onValueChange={(value) => form.setValue('statusId', value)}
              disabled={!boardId}
            >
              <SelectTrigger data-testid="forms-create-task-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((status) => (
                  <SelectItem key={status.id ?? ''} value={status.id ?? ''}>
                    {status.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {groups.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                {t('submissions.createTask.groupLabel')}
              </label>
              <Select
                value={form.watch('groupId')}
                onValueChange={(value) => form.setValue('groupId', value)}
                disabled={!boardId}
              >
                <SelectTrigger data-testid="forms-create-task-group-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {t('submissions.createTask.assigneeLabel')}
            </label>
            <Select
              value={form.watch('assigneeId')}
              onValueChange={(value) => form.setValue('assigneeId', value)}
            >
              <SelectTrigger data-testid="forms-create-task-assignee-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {members.map((member) => (
                  <SelectItem key={member.userId} value={member.userId}>
                    {member.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={createTask.isPending}
              data-testid="forms-create-task-submit-button"
            >
              {t('submissions.createTask.submitButton')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
